/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { SALE_STATE_STYLES, type SaleState } from './sale-states';

/**
 * One column per state, unlike the lease board's five-from-seven.
 *
 * Nothing to group here: the sale has exactly three states, and each is a
 * different answer to "where is this sale" — running, done, undone. A
 * column per state also means the header count and the state badge can
 * never disagree, so the badge is left off the cards below.
 */
export const SALE_BOARD_COLUMNS: readonly SaleState[] = [
  'in_paperwork', 'completed', 'voided',
] as const;

/**
 * Group sales into the board's columns, preserving the incoming order.
 *
 * A sale carrying some other state — a value left by an earlier migration
 * — is dropped from the board rather than piled into a catch-all: the
 * table view still lists it, and `sale-states.ts` already treats anything
 * outside the three as read-only for the same reason.
 *
 * @param sales - The sales to group, already filtered and searched.
 * @returns One bucket per column, in board order.
 */
export function groupSalesByState<T extends { state: string }>(
  sales: readonly T[],
): Array<{ state: SaleState; sales: T[] }> {
  const buckets = new Map<SaleState, T[]>(
    SALE_BOARD_COLUMNS.map((state) => [state, [] as T[]]),
  );
  sales.forEach((sale) => {
    buckets.get(sale.state as SaleState)?.push(sale);
  });
  return SALE_BOARD_COLUMNS.map((state) => ({
    state,
    sales: buckets.get(state) ?? [],
  }));
}

/**
 * Resolve the column heading for a state.
 *
 * Reuses `states.*` rather than adding a `board.columns.*` set: the column
 * *is* the state here, so a second vocabulary would be two names for one
 * thing, free to drift apart at the next translation pass.
 *
 * @returns A translator from state to localized label.
 */
export function useSaleBoardColumnLabel(): (state: SaleState) => string {
  const { t } = useTranslation('features/sales');
  return (state) => t(`states.${state}`, { defaultValue: state });
}

export { SALE_STATE_STYLES };
